import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Preloader from '../Preloader';
import StoryCard from '../TrendingStories/StoryCard';

import StoriesSvc from '../../services/StoriesSvc';

import { getMetaTag } from '../../helpers/helpers';
import { removeDuplicates } from '../../helpers/removeDuplicates';

import Styles from './styles.module.scss';

function setMetaData(story) {
  document.title = `CityFALCON - ${story.title}`;
  getMetaTag('name', 'title').setAttribute('content', story.title);
  getMetaTag('name', 'description').setAttribute('content', story.description || story.title);
}

const SimilarStoriesSinglePage = () => {
  const [stories, setStories] = useState({
    mainStory: null,
    similarStories: [],
    loaded: false,
  });
  const [newStoriesLoading, setNewStoriesLoading] = useState(false);
  const pageToken = useRef('EOD');
  const currentRequest = useRef(null);
  const dataLoading = useRef(false);
  const storiesRef = useRef();
  const { filterAllLanguages, filterLanguages, user } = useSelector(({
    storyFilters,
    userPreferencesReducer,
  }) => ({
    user: userPreferencesReducer.user,
    filterAllLanguages: storyFilters.all_languages,
    filterLanguages: storyFilters.languages,
  }));

  const allLanguagesRef = useRef(filterAllLanguages);
  const languagesRef = useRef(filterLanguages);
  useEffect(() => {
    allLanguagesRef.current = filterAllLanguages;
  }, [filterAllLanguages]);
  useEffect(() => {
    languagesRef.current = filterLanguages;
  }, [filterLanguages]);

  const { story_uuid: uuid } = useParams();

  const fetchStories = useCallback(async (nextPage) => {
    if (dataLoading.current) {
      return;
    }

    dataLoading.current = true;

    const mainStory = await StoriesSvc.getSingleStory(uuid);
    if (!mainStory) {
      window.location.href = '/404';
      return;
    }

    if (nextPage) {
      setNewStoriesLoading(true);
    }

    const params = {
      limit: 20,
      ...(pageToken.current !== 'EOD' && {
        page_token: pageToken.current,
      }),
      ...(allLanguagesRef.current
        ? { all_languages: true } : { languages: languagesRef.current }),
      with_sentiments: true,
    };
    const similarStories = await StoriesSvc.getSimilarStoriesDirectly(mainStory, params);

    dataLoading.current = false;
    pageToken.current = similarStories.next_page_token || 'EOD';
    setStories(({ similarStories: currentStories }) => ({
      loaded: true,
      mainStory,
      similarStories: removeDuplicates([
        mainStory,
        ...currentStories,
        ...similarStories.stories,
      ], 'id').sort((a, b) => (
        new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime()
      )),
    }));
    setNewStoriesLoading(false);
    setMetaData(mainStory);
  }, [uuid, filterAllLanguages, filterLanguages]);

  useEffect(() => {
    fetchStories();
    const onScroll = () => {
      const docTop = storiesRef.current.offsetTop;
      const docHeight = storiesRef.current.offsetHeight;
      const winheight = window.innerHeight === undefined
        ? document.documentElement.clientHeight
        : window.innerHeight;
      const scrollpoint = window.scrollY === undefined
        ? window.document.documentElement.scrollTop
        : window.scrollY;

      if (
        (scrollpoint + winheight) >= (docHeight + docTop)
        && pageToken.current !== 'EOD'
      ) {
        fetchStories(true);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      currentRequest.current?.cancel?.();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const hideStory = useCallback((id) => {
    setStories((currentStories) => ({
      ...currentStories,
      similarStories: currentStories.similarStories.filter((story) => story.id !== id),
    }));
  }, []);

  if (!stories.loaded) {
    return <Preloader loading static centered />;
  }

  const handleLikeChange = (data) => {
    const { id, liked, disliked } = data;
    setStories((currentStories) => ({
      ...currentStories,
      similarStories: currentStories.similarStories.map((story) => {
        if (story.id === id) {
          return {
            ...story,
            liked,
            disliked,
          };
        }
        return story;
      }),
    }));
  };

  const handleBookmarkClick = (data) => {
    const { id, bookmarked } = data;
    setStories((currentStories) => ({
      ...currentStories,
      similarStories: currentStories.similarStories.map((story) => {
        if (story.id === id) {
          return {
            ...story,
            bookmarked,
          };
        }
        return story;
      }),
    }));
  };

  return (
    <section className={Styles['similar-stories']}>
      <h1 className={Styles['similar-stories-title']}>
        {stories.mainStory.title}
        {stories.mainStory.domain_name && (
          <>
            {' '}
            |
            {' '}
            {stories.mainStory.domain_name}
          </>
        )}
      </h1>
      <div className={Styles['similar-stories-list']} ref={storiesRef}>
        {Boolean(stories?.similarStories?.length) && (
          <h2 className={Styles['similar-stories-subtitle']}>Stories</h2>
        )}
        {
          stories.similarStories.map((story) => (
            <StoryCard
              key={story.id}
              user={user}
              type={story.type}
              story={story}
              onHide={hideStory}
              onFreezeStories={() => { }}
              forceRowsView
              onLikeChanged={handleLikeChange}
              onBookmarkClick={handleBookmarkClick}
            />
          ))
        }
        {Boolean(stories?.similarStories?.length) && newStoriesLoading && (
          <Preloader loading static centered />
        )}
      </div>
    </section>
  );
};

export default SimilarStoriesSinglePage;
